import { format } from 'date-fns';

import { config } from '../../api/config';
import { Api } from '../../api/api';
import {
  generateHeader,
  generatePhoneQuery,
  getTimestamp,
  mapPhysicalCardOwnerType,
  mapPhysicalCardStatus,
} from '../../utils';

export const OWNER_TYPE_PRIVATE = 1351;
export const OWNER_TYPE_BUSINESS = 1352;
export const MIN_OWNER_SEARCH_LENGTH = config.DEFAULTS.symbolsToSearch;

export const STATUS_OPTIONS = generateHeader(
  [0, 1, 2, 3],
  mapPhysicalCardStatus
);

export const OWNER_TYPE_OPTIONS = generateHeader(
  [OWNER_TYPE_PRIVATE, OWNER_TYPE_BUSINESS],
  mapPhysicalCardOwnerType
);

export const MEMBERSHIPS_FETCH_PARAMS = {
  limit: 1000,
  offset: 0,
  order: [{ by: 'title_lt', order: 'asc' }],
};

export const buildMembershipOptions = (memberships = []) => [
  { name: '—', value: '' },
  ...memberships.map(membership => ({
    name: membership.title_lt || String(membership.id),
    value: membership.id,
  })),
];

export const getMembershipLabel = (item = {}) => {
  if (item.membership && item.membership.title_lt) {
    return item.membership.title_lt;
  }
  return '-';
};

export const isExactCardMatch = (cards, number) => {
  const normalized = number.trim().toUpperCase();
  return (cards || []).some(
    card => (card.number || '').trim().toUpperCase() === normalized
  );
};

export const splitTimestampToDateTime = timestamp => {
  if (timestamp == null) {
    return { date: '', time: '' };
  }

  const dateObj = new Date(timestamp);
  if (Number.isNaN(dateObj.getTime())) {
    return { date: '', time: '' };
  }

  return {
    date: format(dateObj, 'yyyy-MM-dd'),
    time: format(dateObj, 'HH:mm'),
  };
};

export const searchPrivateOwners = async query => {
  const queryToSubmit = generatePhoneQuery(query.trim());
  const results = await Api.searchClients(queryToSubmit);
  const clients = Array.isArray(results) ? results : [];

  return clients.map(client => ({
    value: client.id,
    label: client.phone || String(client.id),
  }));
};

export const searchBusinessOwners = async query => {
  const trimmed = query.trim();
  const { business_clients } = await Api.getBusinessClients({
    limit: 10,
    order: [{ by: 'name', order: 'asc' }],
    filter: {
      and: [
        {
          field: 'name',
          value: `%${trimmed}%`,
          expression: 'iLike',
        },
      ],
    },
  });

  return (business_clients || []).map(client => ({
    value: client.id,
    label: client.name || String(client.id),
  }));
};

export const searchOwnersByType = async (query, ownerType) => {
  if (Number(ownerType) === OWNER_TYPE_BUSINESS) {
    return searchBusinessOwners(query);
  }
  return searchPrivateOwners(query);
};

export const buildDiscountPayload = formState => {
  if (!formState.membership_id) {
    return null;
  }

  return {
    membership_id: Number(formState.membership_id),
    discount_valid_from: getTimestamp(
      formState.discount_valid_from,
      formState.discount_valid_from_time || '00:00'
    ),
    discount_valid_to: formState.discount_valid_to
      ? getTimestamp(
          formState.discount_valid_to,
          formState.discount_valid_to_time || '23:59'
        )
      : null,
  };
};

export const EMPTY_DISCOUNT_FORM = {
  membership_id: '',
  discount_valid_from: '',
  discount_valid_from_time: '',
  discount_valid_to: '',
  discount_valid_to_time: '',
};

const FORM_COMPARE_KEYS = ['status', 'owner_type', 'owner_id'];

export const isPhysicalCardFormEqual = (a, b) => {
  if (!a || !b) return false;

  return FORM_COMPARE_KEYS.every(
    key => String(a[key] ?? '') === String(b[key] ?? '')
  );
};

export const cardToFormValues = card => ({
  number: card.number || '',
  owner_type: card.owner_type || OWNER_TYPE_PRIVATE,
  owner_id: card.owner_id != null ? card.owner_id : null,
  ownerQuery: card.owner_label || '',
  membership_id: '',
  discount_valid_from: '',
  discount_valid_from_time: '',
  discount_valid_to: '',
  discount_valid_to_time: '',
  initial_balance: '0',
  status: card.status != null ? card.status : 1,
});
